import { create } from 'zustand';
import { Auth, setAuthTokens, getRefreshToken, refreshTokensNow, type User } from '@/lib/api';
import { clearStateTimer } from '@/lib/useStateTimer';

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  /** Initial session restore in flight — UI gösterimini bekletir, yoksa
   *  refresh sonrası race condition ile kullanıcı bir an Login'e atılır. */
  restoring: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Try to restore session from refresh token (OS keyring or localStorage). */
  restore: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  token: null,
  loading: false,
  restoring: true,

  login: async (username, password) => {
    set({ loading: true });
    try {
      const { access_token, refresh_token, user } = await Auth.login(username, password);
      await setAuthTokens(access_token, refresh_token);
      set({ user, token: access_token, loading: false });
    } catch (e) {
      set({ loading: false });
      throw e;
    }
  },

  logout: async () => {
    try { await Auth.logout(); } catch { /* */ }
    await setAuthTokens(null, null);
    clearStateTimer();
    set({ user: null, token: null });
  },

  restore: async () => {
    try {
      const rt = await getRefreshToken();
      if (!rt) return;
      // Explicit refresh first → avoids the noisy 401 from /me without a token.
      const access = await refreshTokensNow();
      if (!access) return;
      const { user } = await Auth.me();
      if (user) set({ user, token: access });
    } catch {
      await setAuthTokens(null, null);
      set({ user: null, token: null });
    } finally {
      set({ restoring: false });
    }
  },
}));
